/**
 * Admin Service
 * Serviço para operações administrativas
 */

import { supabase } from '../supabaseClient';
import { logger } from '../logger';
import { PLANS } from '../subscriptionService';

/**
 * Verifica se o usuário é superusuário
 */
export async function checkIsSuperuser(userId) {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('is_superuser')
      .eq('id', userId)
      .single();

    if (error) {
      logger.error('Erro ao verificar superusuário:', error);
      return false;
    }

    return data?.is_superuser === true;
  } catch (error) {
    logger.error('Erro ao verificar superusuário:', error);
    return false;
  }
}

/**
 * Obtém estatísticas gerais do sistema
 */
export async function getSystemStats() {
  try {
    // Total de usuários
    const { count: totalUsers } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true });

    // Usuários ativos (com sessão nos últimos 30 dias)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    // Total de assinaturas ativas
    const { count: activeSubscriptions } = await supabase
      .from('subscriptions')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'active');

    // Total de conversas
    const { count: totalConversations } = await supabase
      .from('chat_topics')
      .select('*', { count: 'exact', head: true });

    // Total de mensagens
    const { count: totalMessages } = await supabase
      .from('chat_messages')
      .select('*', { count: 'exact', head: true });

    // Conversas hoje
    const today = new Date().toISOString().split('T')[0];
    const { count: conversationsToday } = await supabase
      .from('chat_topics')
      .select('*', { count: 'exact', head: true })
      .gte('created_at', today);

    // Mensagens hoje
    const { count: messagesToday } = await supabase
      .from('chat_messages')
      .select('*', { count: 'exact', head: true })
      .gte('created_at', today);

    return {
      totalUsers: totalUsers || 0,
      activeSubscriptions: activeSubscriptions || 0,
      totalConversations: totalConversations || 0,
      totalMessages: totalMessages || 0,
      conversationsToday: conversationsToday || 0,
      messagesToday: messagesToday || 0,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    logger.error('Erro ao buscar estatísticas:', error);
    throw error;
  }
}

/**
 * Lista todos os usuários com suas assinaturas
 */
export async function getAllUsersWithSubscriptions(page = 1, limit = 20) {
  try {
    const offset = (page - 1) * limit;

    // Busca usuários com suas assinaturas
    const { data: users, error, count } = await supabase
      .from('users')
      .select(`
        *,
        subscriptions (
          id,
          plan_id,
          status,
          billing_cycle,
          stripe_subscription_id,
          stripe_customer_id,
          current_period_start,
          current_period_end,
          cancel_at_period_end,
          created_at
        )
      `, { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;

    // Enriquecer com informações do plano
    const enrichedUsers = users.map(user => {
      const subscription = user.subscriptions?.[0]; // Pega a assinatura mais recente
      const planId = subscription?.plan_id || 'free';
      const plan = PLANS[planId] || PLANS.free;

      // Preços dos planos
      const planPrices = {
        free: { monthly: 0, yearly: 0 },
        basic: { monthly: 87.90, yearly: 916.97 },
        pro: { monthly: 119.90, yearly: 1222.97 },
        master: { monthly: 157.90, yearly: 1660.07 },
      };

      const price = planPrices[planId]?.[subscription?.billing_cycle || 'monthly'] || 0;

      return {
        ...user,
        subscription: subscription || null,
        plan: {
          ...plan,
          price,
          billingCycle: subscription?.billing_cycle || 'monthly',
        },
      };
    });

    return {
      users: enrichedUsers,
      total: count || 0,
      page,
      limit,
      totalPages: Math.ceil((count || 0) / limit),
    };
  } catch (error) {
    logger.error('Erro ao listar usuários:', error);
    throw error;
  }
}

/**
 * Lista usuários pendentes de confirmação de email
 */
export async function getPendingEmailConfirmations() {
  try {
    // Busca usuários que não confirmaram email (usando auth.users via RPC ou view)
    // Como não temos acesso direto ao auth.users, buscamos usuários recentes
    // e verificamos pelo metadata ou criamos uma view no Supabase

    const { data: users, error } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100);

    if (error) throw error;

    // Filtra usuários que podem estar pendentes (criados mas sem atividade)
    // Idealmente isso seria feito com uma view no Supabase
    const pending = users.filter(user => {
      const createdAt = new Date(user.created_at);
      const now = new Date();
      const hoursSinceCreation = (now - createdAt) / (1000 * 60 * 60);

      // Considera pendente se criado há menos de 24h e sem dados completos
      return hoursSinceCreation < 24 && (!user.company || !user.type_business);
    });

    return pending;
  } catch (error) {
    logger.error('Erro ao buscar confirmações pendentes:', error);
    throw error;
  }
}

/**
 * Lista usuários ativos no chat (com conversas abertas)
 */
export async function getActiveChattingUsers() {
  try {
    const { data, error } = await supabase
      .from('chat_topics')
      .select(`
        user_id,
        users!inner (
          id,
          email,
          full_name,
          company
        )
      `)
      .order('updated_at', { ascending: false });

    if (error) throw error;

    // Agrupa por usuário e conta conversas
    const userMap = new Map();

    data.forEach(topic => {
      const userId = topic.user_id;
      if (userMap.has(userId)) {
        userMap.get(userId).chatCount++;
      } else {
        userMap.set(userId, {
          ...topic.users,
          chatCount: 1,
        });
      }
    });

    return Array.from(userMap.values()).sort((a, b) => b.chatCount - a.chatCount);
  } catch (error) {
    logger.error('Erro ao buscar usuários ativos:', error);
    throw error;
  }
}

/**
 * Obtém dados para gráficos de crescimento de usuários
 */
export async function getUserGrowthData(days = 30) {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
      .from('users')
      .select('created_at')
      .gte('created_at', startDate.toISOString())
      .order('created_at', { ascending: true });

    if (error) throw error;

    // Agrupa por dia
    const dailyData = {};
    data.forEach(user => {
      const date = user.created_at.split('T')[0];
      dailyData[date] = (dailyData[date] || 0) + 1;
    });

    // Preenche dias vazios
    const result = [];
    const currentDate = new Date(startDate);
    const today = new Date();

    while (currentDate <= today) {
      const dateStr = currentDate.toISOString().split('T')[0];
      result.push({
        date: dateStr,
        count: dailyData[dateStr] || 0,
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return result;
  } catch (error) {
    logger.error('Erro ao buscar dados de crescimento:', error);
    throw error;
  }
}

/**
 * Obtém dados para gráficos de uso do chat
 */
export async function getChatUsageData(days = 30) {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
      .from('chat_messages')
      .select('created_at, role')
      .gte('created_at', startDate.toISOString())
      .order('created_at', { ascending: true });

    if (error) throw error;

    // Agrupa por dia
    const dailyData = {};
    data.forEach(msg => {
      const date = msg.created_at.split('T')[0];
      if (!dailyData[date]) {
        dailyData[date] = { userMessages: 0, assistantMessages: 0 };
      }
      if (msg.role === 'user') {
        dailyData[date].userMessages++;
      } else {
        dailyData[date].assistantMessages++;
      }
    });

    // Preenche dias vazios
    const result = [];
    const currentDate = new Date(startDate);
    const today = new Date();

    while (currentDate <= today) {
      const dateStr = currentDate.toISOString().split('T')[0];
      result.push({
        date: dateStr,
        userMessages: dailyData[dateStr]?.userMessages || 0,
        assistantMessages: dailyData[dateStr]?.assistantMessages || 0,
        total: (dailyData[dateStr]?.userMessages || 0) + (dailyData[dateStr]?.assistantMessages || 0),
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return result;
  } catch (error) {
    logger.error('Erro ao buscar dados de uso do chat:', error);
    throw error;
  }
}

/**
 * Obtém distribuição de planos
 */
export async function getPlanDistribution() {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('plan_id, status')
      .eq('status', 'active');

    if (error) throw error;

    const distribution = {
      free: 0,
      basic: 0,
      pro: 0,
      master: 0,
    };

    data.forEach(sub => {
      const planId = sub.plan_id || 'free';
      if (distribution[planId] !== undefined) {
        distribution[planId]++;
      }
    });

    return Object.entries(distribution).map(([name, value]) => ({
      name: PLANS[name]?.name || name,
      value,
      planId: name,
    }));
  } catch (error) {
    logger.error('Erro ao buscar distribuição de planos:', error);
    throw error;
  }
}

/**
 * Obtém receita estimada
 */
export async function getRevenueEstimate() {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('plan_id, billing_cycle')
      .eq('status', 'active');

    if (error) throw error;

    const planPrices = {
      free: { monthly: 0, yearly: 0 },
      basic: { monthly: 87.90, yearly: 916.97 },
      pro: { monthly: 119.90, yearly: 1222.97 },
      master: { monthly: 157.90, yearly: 1660.07 },
    };

    let monthlyRevenue = 0;
    let yearlyRevenue = 0;

    data.forEach(sub => {
      const planId = sub.plan_id || 'free';
      const cycle = sub.billing_cycle || 'monthly';
      const price = planPrices[planId]?.[cycle] || 0;

      if (cycle === 'monthly') {
        monthlyRevenue += price;
      } else {
        yearlyRevenue += price;
      }
    });

    // MRR (Monthly Recurring Revenue)
    const mrr = monthlyRevenue + (yearlyRevenue / 12);

    return {
      monthlyRevenue,
      yearlyRevenue,
      mrr: Math.round(mrr * 100) / 100,
      arr: Math.round(mrr * 12 * 100) / 100,
      totalSubscriptions: data.length,
    };
  } catch (error) {
    logger.error('Erro ao calcular receita:', error);
    throw error;
  }
}
